// C:\IntegraNube_WebStore\src\api\api.js
/* ------------------------------------------------------------------
 * IntegraNube WebStore - cliente Axios centralizado y resiliente
 *
 * Objetivos de esta versión:
 *  - Mantener una única instancia Axios para toda la WebStore.
 *  - Identificar la tienda mediante X-Store-Domain.
 *  - Reintentar automáticamente SOLO lecturas seguras (GET / HEAD).
 *  - Recuperarse de fallas transitorias de red, timeout y 5xx.
 *  - NO reintentar automáticamente POST/PUT/PATCH/DELETE para evitar
 *    duplicar pedidos, pagos u otras escrituras.
 *  - Mantener el error original de Axios y agregar friendlyMessage.
 *
 * IMPORTANTE:
 *  - Esta versión NO implementa caché/localStorage de /tienda.
 * ------------------------------------------------------------------ */

import axios from 'axios';

/* =====================================================================
 * CONFIGURACIÓN
 * =================================================================== */

const API_BASE_URL =
  process.env.REACT_APP_API_URL?.replace(/\/+$/, '') ||
  'https://store.integranube.cl';

const DEFAULT_TIMEOUT_MS = Math.max(
  1000,
  Number(process.env.REACT_APP_API_TIMEOUT_MS || 7000)
);

/*
 * Cantidad de REINTENTOS después del intento inicial.
 * 4 => hasta 5 intentos totales.
 */
const DEFAULT_RETRIES = Math.max(
  0,
  Number(process.env.REACT_APP_API_RETRIES || 4)
);

const RETRY_BASE_DELAY_MS = 450;
const RETRY_MAX_DELAY_MS = 3000;

const SAFE_RETRY_METHODS = new Set(['get', 'head']);
const RETRYABLE_STATUS = new Set([408, 425, 429, 500, 502, 503, 504]);

/* =====================================================================
 * DOMINIO DE LA STORE
 * =================================================================== */

function normalizeDomain(value) {
  let domain = String(value || '').trim().toLowerCase();

  if (!domain) return '';

  domain = domain
    .replace(/^https?:\/\//i, '')
    .split('/')[0]
    .split(':')[0]
    .replace(/\.$/, '');

  return domain;
}

export function getStoreDomain() {
  if (typeof window === 'undefined') {
    return normalizeDomain(process.env.REACT_APP_DEV_STORE_DOMAIN || '');
  }

  const browserDomain = normalizeDomain(window.location.hostname);

  const isDevelopmentHost =
    browserDomain === 'localhost' ||
    browserDomain === '127.0.0.1' ||
    browserDomain === '0.0.0.0';

  if (isDevelopmentHost) {
    return normalizeDomain(
      process.env.REACT_APP_DEV_STORE_DOMAIN || ''
    );
  }

  return browserDomain;
}

/* =====================================================================
 * AXIOS
 * =================================================================== */

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: DEFAULT_TIMEOUT_MS,
  withCredentials: false,
});

/* =====================================================================
 * REQUEST INTERCEPTOR
 * =================================================================== */

api.interceptors.request.use((config) => {
  config.headers = config.headers || {};

  const storeDomain = getStoreDomain();

  if (storeDomain) {
    config.headers['X-Store-Domain'] = storeDomain;
  }

  if (config.data && !config.headers['Content-Type']) {
    config.headers['Content-Type'] = 'application/json';
  }

  return config;
});

/* =====================================================================
 * RETRY HELPERS
 * =================================================================== */

function sleep(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function isCanceled(error) {
  return (
    axios.isCancel?.(error) ||
    error?.code === 'ERR_CANCELED' ||
    error?.name === 'CanceledError'
  );
}

function isSafeRetryMethod(config) {
  const method = String(config?.method || 'get').toLowerCase();
  return SAFE_RETRY_METHODS.has(method);
}

function shouldRetry(error) {
  const config = error?.config;

  if (!config || isCanceled(error) || !isSafeRetryMethod(config)) {
    return false;
  }

  /*
   * Permite desactivar retry en una solicitud concreta:
   *
   * api.get('/ruta', { retry: false })
   */
  if (config.retry === false) {
    return false;
  }

  const status = error?.response?.status;

  /*
   * Sin response significa normalmente:
   * - error de red
   * - conexión TCP que no pudo establecerse
   * - timeout
   * - ERR_NETWORK
   */
  if (!status) {
    return true;
  }

  return RETRYABLE_STATUS.has(Number(status));
}

function getRetryLimit(config) {
  const customRetries = Number(config?.retries);

  if (Number.isFinite(customRetries) && customRetries >= 0) {
    return Math.floor(customRetries);
  }

  return DEFAULT_RETRIES;
}

function getRetryAfterMs(error) {
  const value = error?.response?.headers?.['retry-after'];

  if (!value) return 0;

  const seconds = Number(value);

  if (Number.isFinite(seconds) && seconds >= 0) {
    return Math.min(seconds * 1000, 10000);
  }

  const date = Date.parse(value);

  if (Number.isFinite(date)) {
    return Math.min(Math.max(0, date - Date.now()), 10000);
  }

  return 0;
}

function getRetryDelayMs(retryNumber, error) {
  const retryAfterMs = getRetryAfterMs(error);

  if (retryAfterMs > 0) {
    return retryAfterMs;
  }

  const exponential = Math.min(
    RETRY_BASE_DELAY_MS * Math.pow(2, Math.max(0, retryNumber - 1)),
    RETRY_MAX_DELAY_MS
  );

  /* jitter para que varios clientes no reintenten exactamente al mismo tiempo */
  const jitter = Math.floor(Math.random() * 250);

  return exponential + jitter;
}

function buildFriendlyMessage(error) {
  const status = error?.response?.status;

  if (!error?.response) {
    if (error?.code === 'ECONNABORTED') {
      return 'La conexión está demorando más de lo esperado. Intente nuevamente.';
    }

    return 'No fue posible conectar con la tienda. Verifique su conexión e intente nuevamente.';
  }

  if (status === 429) {
    return 'Hay demasiadas solicitudes en este momento. Intente nuevamente en unos segundos.';
  }

  if ([500, 502, 503, 504].includes(Number(status))) {
    return 'El servicio está temporalmente ocupado. Intente nuevamente en unos segundos.';
  }

  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    'No fue posible completar la solicitud.'
  );
}

/* =====================================================================
 * RESPONSE + RETRY INTERCEPTOR
 * =================================================================== */

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error?.config;

    if (shouldRetry(error)) {
      const maxRetries = getRetryLimit(config);
      const retriesDone = Number(config.__webStoreRetryCount || 0);

      if (retriesDone < maxRetries) {
        const retryNumber = retriesDone + 1;
        const delayMs = getRetryDelayMs(retryNumber, error);

        config.__webStoreRetryCount = retryNumber;

        const method = String(config.method || 'get').toUpperCase();
        const url = config.url || '';

        console.warn(
          `[WebStore API] RETRY ${method} ${url} ` +
          `${retryNumber}/${maxRetries} en ${delayMs}ms`,
          error?.code || error?.response?.status || 'NETWORK'
        );

        /*
         * App.js puede usar onRetry para actualizar el mensaje del loader.
         * Es opcional y no modifica el comportamiento del resto de páginas.
         */
        if (typeof config.onRetry === 'function') {
          try {
            config.onRetry({
              retryNumber,
              maxRetries,
              delayMs,
              error,
            });
          } catch (callbackError) {
            console.warn('[WebStore API] Error en callback onRetry:', callbackError);
          }
        }

        await sleep(delayMs);

        return api(config);
      }
    }

    error.friendlyMessage = buildFriendlyMessage(error);
    error.retryCount = Number(config?.__webStoreRetryCount || 0);
    error.retryExhausted = error.retryCount > 0;

    const method = String(config?.method || 'get').toUpperCase();
    const url = config?.url || '';

    if (!error?.response) {
      console.error(
        `[WebStore API] NETWORK ${method} ${url}`,
        error?.message || error
      );
    } else {
      console.error(
        `[WebStore API] HTTP ${error.response.status} ${method} ${url}`,
        error?.response?.data || error?.message
      );
    }

    return Promise.reject(error);
  }
);

/* =====================================================================
 * HELPERS
 * =================================================================== */

export const fetchData = (endpoint, params = {}, config = {}) =>
  api
    .get(endpoint, {
      ...config,
      params,
    })
    .then((response) => response.data);

export const postData = (endpoint, body = {}, config = {}) =>
  api.post(endpoint, body, config).then((response) => response.data);

export const putData = (endpoint, body = {}, config = {}) =>
  api.put(endpoint, body, config).then((response) => response.data);

export const deleteData = (endpoint, config = {}) =>
  api.delete(endpoint, config).then((response) => response.data);

export default api;
